import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import SegmentedButtonTsoPq from '@components/Buttons/SegmentedButtonTsoPq';
import ChipSelectorTsoPq from '@components/ChipSelector/ChipSelectorTsoPq';
import DatepickerTsoPq from '@components/Datepicker/DatepickerTsoPq';
import { SidePanelPageLayoutTsoPq, useSidePanel } from '@components/Grid/GridTsoPq';
import { SidePanelTsoPq } from '@components/Grid/SidePanelTsoPq';
import ImagePlaceholderTsoPq from '@components/ImagePlaceholder/ImagePlaceholderTsoPq';
import LoadingSpinnerTsoPq from '@components/LoadingSpinners/LoadingSpinnerTsoPq';
import SelectTsoPq from '@components/Select/SelectTsoPq';
import TreeAutocompleteTsoPq from '@components/TreeAutocomplete/TreeAutocompleteTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { parseSubstationDevices, TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import { Box, styled } from '@mui/material';
import { dateTimeValueFormatter } from '@pages/Masterdata/StyledGridPageComponents';
import {
  defaultTsos,
  StyledButtonWrapper,
  StyledLineWrapper,
  StyledMeasurementDeviceTitle,
  StyledWrapper,
  TsosStateProps,
} from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';
import { StyledSectionWrapper } from '@pages/MeasurmentExport/MeasurmentExportStyles';
import { useGetAggregationIntervalGetAllQuery } from '@redux/apiSlices/aggregationIntervalApi';
import { useGetMeasurementDeviceGetAllQuery } from '@redux/apiSlices/measurementDeviceApi';
import {
  ApiCommonExternalDtoMeasurementLogExternalDto,
  PostMeasurementLogGetSelectedApiResponse,
  usePostMeasurementLogGetSelectedMutation,
} from '@redux/apiSlices/measurementLogApi';
import {
  ApiDatabaseDtOsSubstationsDeviceHierarchyDto,
  useLazyGetSubstationGetSubstationDeviceHierarchyQuery,
} from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
  enhancedMeasurementLog,
  resetAll,
  selectActiveTsos,
  selectAggregationInterval,
  selectAggregationIntervalType,
  selectDataToBeDisplayedInChart,
  selectDeviceTechnicalNames,
  selectEnd,
  selectMeasurementLogs,
  selectStart,
  setActiveTsoName,
  setActiveTsos,
  setAggregationInterval,
  setAggregationIntervalType,
  setDataToBeDisplayedInChart,
  setDeviceTechnicalNames,
  setEnd,
  setMeasurementLogs,
  setStart,
} from '@redux/slices/importedMeasurementsOverviewSlice';
import { findDeviceAndSubstation } from '@utils/deviceHierarchy';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImportedMeasurementsOverviewChart } from './ImportedMeasurementsOverviewChart';
import { StyledChartWrapper, StyledTitleAndActions, StyledTitleWrapper } from './importedMeasurementsOverviewStyles';

type AggregationIntervalProps = {
  id: number;
  label: string;
  value: string;
};
type Option = {
  id: number;
  value?: string;
  label: string;
};

export const ImportedMeasurementsOverview = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: tsos } = useGetTsoGetAllQuery();
  const { data: measurementDevices } = useGetMeasurementDeviceGetAllQuery();
  const { data: aggregationIntervals } = useGetAggregationIntervalGetAllQuery();
  const [aggregationIntervalsOptions, setAggregationIntervalsOptions] = useState<AggregationIntervalProps[]>([]);

  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const [submitMeasurementLog, { isLoading }] = usePostMeasurementLogGetSelectedMutation();
  const [triedSubmitting, setTriedSubmitting] = useState<boolean>(false);
  const [rawTsoDeviceTree, setRawTsoDeviceTree] = useState<{
    [key: number]: ApiDatabaseDtOsSubstationsDeviceHierarchyDto[] | undefined;
  }>({});
  const [tsoDeviceOptions, setTsoDeviceOptions] = useState<{
    [key: number]: TreeListItemTsoPqProps[] | undefined;
  }>({});
  const [mandatoryFieldsStatus, setMandatoryFieldsStatus] = useState<{
    tso: boolean | undefined;
    start: boolean | undefined;
    end: boolean | undefined;
    aggInterval: boolean | undefined;
    aggType: boolean | undefined;
    selectedDevices: { [key: string]: boolean | undefined };
  }>({
    tso: undefined,
    start: undefined,
    end: undefined,
    aggInterval: undefined,
    aggType: undefined,
    selectedDevices: {},
  });
  const [technicalNames, setTechnicalNames] = useState({});

  const StyledSegmentedButtonTsoPq = styled(SegmentedButtonTsoPq)(() => {
    return {
      display: 'flex',
    };
  });

  const StyledAggregationWrapper = styled(Box)(() => {
    return {
      display: 'flex',
      gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_24,
      alignItems: mandatoryFieldsStatus.aggInterval === false ? 'center' : 'flex-end',
    };
  });

  const aggIntervalTenMinutes = 'c10m';
  const aggregationIntervalTypeSuffixes = [
    { key: '_c', label: t('measurementVisualization.parameters.current') },
    { key: '_v', label: t('measurementVisualization.parameters.voltage') },
  ];

  const aggregationTypeOptions = aggregationIntervalTypeSuffixes.map((type) => ({
    segmentValue: `${aggIntervalTenMinutes}${type.key}`,
    segmentLabel: type.label,
  }));

  const setKeyToID = aggregationIntervals?.externalDtos?.reduce(
    (acc, interval) => {
      for (const t of aggregationIntervalTypeSuffixes) {
        if (interval.technicalName.includes(t.key) && !acc[t.key]) {
          acc[t.key] = interval.id;
        }
      }
      return acc;
    },
    {} as Record<string, number>
  );

  const handleActiveTsosChange = (tsos: TsosStateProps[]) => {
    const lastActiveTsoName = tsos.at(-1)?.name ?? '';
    const selectedTsosIds = tsos.map((tso) => `${tso.id}`);
    const tsosWithSelectedDevices = Object.keys(deviceTechnicalNames);

    const updatedDeviceTechnicalNames = { ...deviceTechnicalNames };

    const unselectedTsos = tsosWithSelectedDevices.filter((tsoId) => !selectedTsosIds.includes(tsoId));

    unselectedTsos.forEach((tsoId) => {
      delete updatedDeviceTechnicalNames[tsoId];
    });

    dispatch(setDeviceTechnicalNames(updatedDeviceTechnicalNames));
    dispatch(setActiveTsos(tsos));
    dispatch(setActiveTsoName(lastActiveTsoName));
    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      tso: triedSubmitting ? tsos.length > 0 : prev.tso,
    }));

    const reducedTsos = tsos.reduce<{ [key: string]: boolean }>((acc, { id }) => {
      acc[id] = undefined;
      return acc;
    }, {});

    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      selectedDevices: syncMandatoryFieldSelectedDevices(prev.selectedDevices, reducedTsos),
    }));
  };

  const syncMandatoryFieldSelectedDevices = (
    current: { [key: string]: boolean | undefined },
    reference: { [key: string]: boolean | undefined }
  ) => {
    const synced: { [key: string]: boolean | undefined } = {};

    for (const key in reference) {
      synced[key] = key in current ? current[key] : undefined;
    }
    return synced;
  };

  const handleAggregationIntervalChange = (option: Option | null) => {
    dispatch(setAggregationInterval(option ?? ({} as Option)));

    if (option?.value !== aggIntervalTenMinutes) {
      dispatch(setAggregationIntervalType(undefined));
    } else {
      dispatch(setAggregationIntervalType(aggregationTypeOptions[1].segmentValue));
    }

    const isAggTypeRequired = option?.value === aggIntervalTenMinutes;
    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      aggType: !isAggTypeRequired || aggregationIntervalType !== undefined,
    }));
  };

  const handleAggregationTypeChange = (options: string[]) => {
    if (options.length > 0 && options[0] !== aggregationIntervalType) {
      dispatch(setAggregationIntervalType(options[0]));

      const foundAggTypeID = aggregationIntervalTypeSuffixes.find((t) => options[0].includes(t.key));
      dispatch(
        setAggregationInterval({
          id: setKeyToID[foundAggTypeID.key] ?? 0,
          label: aggregationInterval?.label ?? '',
          value: aggregationInterval?.value ?? '',
        })
      );
    }
  };

  const handleStartChange = (date: dayjs.Dayjs) => {
    dispatch(setStart(dayjs(date).format('YYYY-MM-DD HH:mm')));
    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      start: triedSubmitting ? !!date : prev.start,
    }));
  };

  const handleEndChange = (date: dayjs.Dayjs) => {
    dispatch(setEnd(dayjs(date).format('YYYY-MM-DD HH:mm')));
    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      end: triedSubmitting ? !!date : prev.end,
    }));
  };

  const handleDevicesChange = (id: number, selectedDevices: string[]) => {
    const selectedDevicesIds = selectedDevices.map((device) => device.split('-').at(-1));
    const selectTechnicalNames = measurementDevices?.externalDtos
      ?.filter((device) => selectedDevicesIds.includes(`${device.id}`))
      .map((device) => device.technicalName);

    const updatedDeviceTechnicalNames = {
      ...deviceTechnicalNames,
      [id.toString()]: selectedDevices,
    };
    if (selectedDevices.length === 0) {
      updatedDeviceTechnicalNames[id] = [];
    }

    dispatch(setDeviceTechnicalNames(updatedDeviceTechnicalNames));
    setTechnicalNames({ ...technicalNames, [id.toString()]: selectTechnicalNames });

    if (triedSubmitting || selectedDevices.length > 0)
      setMandatoryFieldsStatus((prev) => ({
        ...prev,
        selectedDevices: { ...prev.selectedDevices, [id]: selectedDevices.length > 0 },
      }));
  };

  const activeTsos = useAppSelector(selectActiveTsos);
  const aggregationInterval = useAppSelector(selectAggregationInterval);
  const aggregationIntervalType = useAppSelector(selectAggregationIntervalType);

  const deviceTechnicalNames = useAppSelector(selectDeviceTechnicalNames);
  const start = useAppSelector(selectStart);
  const end = useAppSelector(selectEnd);
  const activeTsoName = useAppSelector(selectActiveTsos).at(-1)?.name ?? '';
  const prevActiveTsosRef = useRef<TsosStateProps[]>([]);
  const measurementLogs = useAppSelector(selectMeasurementLogs);
  const dataToBeDisplayedInChart = useAppSelector(selectDataToBeDisplayedInChart);
  const [resetTrigger, setResetTrigger] = useState<boolean>(false);

  const submitAll = async () => {
    setTriedSubmitting(true);

    const tsoValid = activeTsos.length > 0;
    const startValid = !!start;
    const endValid = !!end;
    const aggIntervalValid = !!aggregationInterval?.id;
    const aggTypeValid = aggregationInterval?.id === 3 || aggregationIntervalType !== '';
    const selectedDevicesValid = Object.values(deviceTechnicalNames).every((devices) => devices?.length > 0);

    const allMandatoryValid =
      tsoValid && startValid && endValid && aggIntervalValid && aggTypeValid && selectedDevicesValid;

    setMandatoryFieldsStatus({
      tso: tsoValid,
      start: startValid,
      end: endValid,
      aggInterval: aggIntervalValid,
      aggType: aggTypeValid,
      selectedDevices: Object.fromEntries(
        Object.entries(deviceTechnicalNames).map(([key, value]) => [key, value?.length > 0])
      ),
    });

    if (!allMandatoryValid) return;

    //@ts-ignore
    const allSelectedDevices = activeTsos.flatMap((tso) => {
      const hierarchy = rawTsoDeviceTree?.[tso.id] ?? [];
      const deviceIds = deviceTechnicalNames[tso.id] ?? [];

      return deviceIds
        .map((fullId) => {
          const deviceEntityId = Number(fullId.split('-').at(-1));
          return findDeviceAndSubstation(hierarchy, deviceEntityId);
        })
        .filter(Boolean);
    });

    const payloads = Object.entries(technicalNames).map(([tsoId, tsoDevicesTechnicalNames]) => ({
      start: dayjs(start).utc().format('YYYY-MM-DD HH:mm'),
      end: dayjs(end).utc().format('YYYY-MM-DD HH:mm'),
      aggregationIntervalId: aggregationInterval?.id,
      aggregationIntervalType: aggregationInterval?.value ?? '',
      aggregationIntervalLabel: aggregationInterval?.label,
      tsoId: Number(tsoId),
      body: tsoDevicesTechnicalNames as string[],
    }));

    try {
      const responses = await Promise.all(
        payloads.filter((payload) => payload.body.length > 0).map((payload) => submitMeasurementLog(payload).unwrap())
      );

      const allMeasurementLogs = responses.flatMap(
        (res: PostMeasurementLogGetSelectedApiResponse) => res.externalDtos ?? []
      );

      dispatch(setMeasurementLogs(allMeasurementLogs));

      dispatch(
        setDataToBeDisplayedInChart(
          allSelectedDevices.map((device: any) => ({
            id: device.deviceId,
            tsoId: device.tsoId,
            start,
            end,
            measurementDeviceId: device.entityId,
            aggregationIntervalId: device.aggregationIntervalId,
            aggregationIntervalType: device.aggregationIntervalType,
            aggregationInterval: device.aggregationIntervalLabel,
            deviceLabel: device.deviceLabel,
            substationLabel: device.substationLabel,
            measurementPointLabel: device.measurementPointLabel,
          })) as enhancedMeasurementLog[]
        )
      );
    } catch (err) {
      console.error('Error submitting measurement logs:', err);
    }
  };

  const handleResetAll = async () => {
    dispatch(resetAll());
    dispatch(setAggregationInterval(computedIntervals[1]));
    dispatch(setAggregationIntervalType(aggregationTypeOptions[1].segmentValue));
    setResetTrigger(true);
    await setMandatoryFieldsStatus({
      tso: undefined,
      start: undefined,
      end: undefined,
      aggInterval: undefined,
      aggType: undefined,
      selectedDevices: {},
    });
    setTriedSubmitting(false);
    setResetTrigger(false);
  };

  useEffect(() => {
    const prevTsoIds = prevActiveTsosRef.current.map((tso) => tso.id);
    const newTso = activeTsos.find((tso) => !prevTsoIds.includes(tso.id));

    if (newTso && !tsoDeviceOptions[newTso.id]) {
      const fetchDevices = async () => {
        try {
          const result = await triggerGetDeviceHierarchy(newTso.id, true);
          const rawTree = result.data?.externalDtos;

          if (rawTree) {
            setRawTsoDeviceTree((prev) => ({ ...prev, [newTso.id]: rawTree }));
            const parsedTree = parseSubstationDevices(rawTree);
            if (parsedTree) {
              setTsoDeviceOptions((prev) => ({ ...prev, [newTso.id]: parsedTree }));
            }
          }
        } catch (error) {
          console.error('Error on "trigger device hierarchy" : ', error);
        }
      };

      fetchDevices();
    }
    prevActiveTsosRef.current = activeTsos;
  }, [activeTsos, activeTsoName]);

  const computedIntervals = useMemo(() => {
    if (!aggregationIntervals?.externalDtos) return [];

    return aggregationIntervals.externalDtos
      .map((interval) => ({
        id: interval.id!,
        label: interval.technicalName.includes(aggIntervalTenMinutes)
          ? t('measurementVisualization.parameters.aggregationinterval.10minutes')
          : t(`measurementVisualization.parameters.${interval.name}`),
        value: interval.technicalName.includes(aggIntervalTenMinutes) ? aggIntervalTenMinutes : interval.technicalName,
      }))
      .filter((item, index, self) => index === self.findIndex((i) => i.label === item.label));
  }, [aggregationIntervals]);

  useEffect(() => {
    setAggregationIntervalsOptions(computedIntervals);
    // set default states
    dispatch(setAggregationInterval(computedIntervals[1]));
    dispatch(setAggregationIntervalType(aggregationTypeOptions[1].segmentValue));
  }, [computedIntervals]);

  const groupedByDevice = useMemo(() => {
    return measurementLogs?.reduce(
      (acc: { [x: string]: any[] }, log: { measurementDeviceId: number }) => {
        if (!acc[log.measurementDeviceId]) {
          acc[log.measurementDeviceId] = [];
        }
        acc[log.measurementDeviceId].push(log);

        return acc;
      },
      {} as Record<number, ApiCommonExternalDtoMeasurementLogExternalDto[]>
    );
  }, [measurementLogs]);

  const renderChartsSection = () => {
    if (dataToBeDisplayedInChart.length === 0) {
      return (
        <ImagePlaceholderTsoPq
          title={t('importedMeasurementOverview.chart.placeholder.title')}
          description={t('importedMeasurementOverview.chart.placeholder.descri')}
        />
      );
    }

    if (isLoading) {
      return <LoadingSpinnerTsoPq />;
    }

    return dataToBeDisplayedInChart.map((chart, index) => {
      const logs = groupedByDevice?.[Number(chart.id)] ?? [];

      return (
        <ImportedMeasurementsOverviewChart
          key={`${chart.id}-${index}`}
          deviceId={Number(chart.measurementDeviceId)}
          measurementLogs={logs}
          tsoId={Number(chart.tsoId)}
          deviceLabel={chart.deviceLabel ?? ''}
          measurementPoint={chart.measurementPointLabel ?? ''}
          substationLabel={chart.substationLabel ?? ''}
          start={dateTimeValueFormatter(chart.start ?? '')}
          end={dateTimeValueFormatter(chart.end ?? '')}
        />
      );
    });
  };

  const SidePanelActions = ({ submitAll, handleResetAll }) => {
    const { handleOpen } = useSidePanel();

    const handleSubmit = () => {
      submitAll();
      handleOpen(false);
    };

    return (
      <StyledButtonWrapper>
        <ButtonTsoPq onClick={handleResetAll} sizeVariant="m" buttonType="outlinedPrimary" textContent={t('measurementVisualization.form.discard')} />
        <ButtonTsoPq onClick={handleSubmit} sizeVariant="m" buttonType="filledPrimary" textContent={t('measurementVisualization.form.submit')} />
      </StyledButtonWrapper>
    );
  };

  return (
    <SidePanelPageLayoutTsoPq
      sidePanel={
        <SidePanelTsoPq
          sectionTitle={t('importedMeasurementOverview.sectionTitle')}
          firstSlot={
            <StyledWrapper>
              <StyledSectionWrapper>
                <ChipSelectorTsoPq
                  customWidth="31rem"
                  label={t('measurementVisualization.parameters.tsoSelect')}
                  chips={tsos?.externalDtos ?? defaultTsos}
                  activeChips={activeTsos}
                  handleChange={handleActiveTsosChange}
                  multiple
                  mandatory
                  error={mandatoryFieldsStatus.tso === false}
                  supportingText={
                    mandatoryFieldsStatus.tso === false
                      ? `${t('measurementVisualization.parameters.tsoSelect')} ${t('general.error.required')}`
                      : ''
                  }
                ></ChipSelectorTsoPq>
                <StyledLineWrapper>
                  <DatepickerTsoPq
                    label={t('importedMeasurementOverview.start')}
                    type={'datetimepicker'}
                    width="15.25rem"
                    enableFutureDates={false}
                    date={start}
                    handleChange={handleStartChange}
                    mandatory
                    error={mandatoryFieldsStatus.start === false}
                    supportingText={
                      mandatoryFieldsStatus.start === false
                        ? `${t('importedMeasurementOverview.start')} ${t('general.error.required')}`
                        : ''
                    }
                  />
                  <DatepickerTsoPq
                    label={t('importedMeasurementOverview.end')}
                    type="datetimepicker"
                    width="15.25rem"
                    enableFutureDates={false}
                    handleChange={handleEndChange}
                    date={end}
                    minDateTime={dayjs(start)}
                    mandatory
                    error={mandatoryFieldsStatus.end === false}
                    supportingText={
                      mandatoryFieldsStatus.end === false
                        ? `${t('importedMeasurementOverview.end')} ${t('general.error.required')}`
                        : ''
                    }
                  />
                </StyledLineWrapper>
                <StyledAggregationWrapper>
                  <SelectTsoPq
                    label={t('measurementVisualization.parameters.aggregationInterval')}
                    placeholder={t('measurementVisualization.parameters.aggregationInterval')}
                    mandatory
                    options={aggregationIntervalsOptions}
                    customwidth="15.25rem"
                    handleChange={handleAggregationIntervalChange}
                    value={aggregationInterval}
                    reset={resetTrigger}
                    error={mandatoryFieldsStatus.aggInterval === false}
                    supportingText={mandatoryFieldsStatus.aggInterval === false ? t('general.error.valueSelect') : ''}
                  />
                  <StyledSegmentedButtonTsoPq
                    disabled={aggregationInterval?.value !== aggIntervalTenMinutes}
                    customWidth="15.25rem"
                    options={aggregationTypeOptions}
                    sizeVariant="m"
                    multipleOptions={false}
                    defaultValue={aggregationInterval?.value === aggIntervalTenMinutes ? aggregationIntervalType : ''}
                    handleChange={handleAggregationTypeChange}
                  />
                </StyledAggregationWrapper>
              </StyledSectionWrapper>
              {activeTsos?.map((tso) => (
                <StyledSectionWrapper key={tso.id} selectDevice>
                  <StyledMeasurementDeviceTitle>{tso.name}</StyledMeasurementDeviceTitle>
                  <TreeAutocompleteTsoPq
                    customwidth="32rem"
                    options={tsoDeviceOptions[tso.id] ?? []}
                    label={t('measurementVisualization.parameters.measurementDevice')}
                    placeholder={t('general.error.valueSelect')}
                    handleChange={(values) => {
                      handleDevicesChange(tso.id, values);
                    }}
                    defaultValue={deviceTechnicalNames?.[tso.id] ?? []}
                    multiple
                    mandatory
                    error={mandatoryFieldsStatus.selectedDevices[tso.id] === false}
                    supportingText={
                      mandatoryFieldsStatus.selectedDevices[tso.id] === false
                        ? `${t('measurementVisualization.parameters.measurementDevice')} ${t('general.error.required')}`
                        : ''
                    }
                  />
                </StyledSectionWrapper>
              ))}
              <SidePanelActions submitAll={submitAll} handleResetAll={handleResetAll} />
            </StyledWrapper>
          }
        ></SidePanelTsoPq>
      }
      content={
        <StyledChartWrapper>
          <StyledTitleAndActions>
            <StyledTitleWrapper>
              <TypographyTsoPq typographyType="pageTitleBold">
                {t('importedMeasurementOverview.pageTitle')}
              </TypographyTsoPq>
              <TypographyTsoPq typographyType="pageSubtitleSM">
                {t('importedMeasurementOverview.pageSubtitle')}
              </TypographyTsoPq>
            </StyledTitleWrapper>
            {/* uncomment when functionality is added */}
            {/* <StyledActionSection>
              <StyledZoomContainer>
                <IconButtonTsoPq
                  icon={faMinus}
                  sizeVariant="m"
                  buttonType="filledPrimary"
                  tooltip={t('importedMeasurementOverview.zoomOut')}
                />
                <IconButtonTsoPq
                  icon={faPlus}
                  sizeVariant="m"
                  buttonType="filledPrimary"
                  tooltip={t('importedMeasurementOverview.zoomIn')}
                />
              </StyledZoomContainer>
              <SegmentedButtonTsoPq
                options={[
                  {
                    segmentValue: 'move',
                    segmentLabel: <FontAwesomeIcon icon={faHand} />,
                    tooltip: t('importedMeasurementOverview.move'),
                  },
                  {
                    segmentValue: 'zoom',
                    segmentLabel: <FontAwesomeIcon icon={faMagnifyingGlass} />,
                    tooltip: t('importedMeasurementOverview.selectPeriod'),
                  },
                ]}
                defaultValue=""
                sizeVariant="m"
              />
              <IconButtonTsoPq
                buttonType="filledNeutral"
                icon={faRotateLeft}
                sizeVariant="m"
                tooltip={t('importedMeasurementOverview.reset')}
              />
            </StyledActionSection> */}
          </StyledTitleAndActions>
          {renderChartsSection()}
        </StyledChartWrapper>
      }
    />
  );
};
